import React, { useState, useEffect } from 'react';
import { 
  Users, 
  UserPlus, 
  UserCheck, 
  UserX, 
  Clock,
  Activity,
  Shield,
  Mail,
  Phone,
  Calendar,
  TrendingUp,
  BarChart3,
  MessageCircle,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2
} from 'lucide-react';
import { useAuth0Context } from '../contexts/Auth0Context';
import { PROCOGIA_COLORS } from '../utils/themeUtils';

const UsersDashboard = () => {
  const { appUser } = useAuth0Context();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    fetchUsersData();
  }, []);

  const fetchUsersData = async () => {
    try {
      // Simulate comprehensive users data
      setStats({
        totalUsers: 127,
        activeUsers: 98,
        adminUsers: 8,
        newUsersThisWeek: 5,
        averageSessionTime: '2.4h',
        loginRate: 92.3
      });

      setUsers([
        {
          id: 1,
          name: 'Sarah Johnson',
          email: 'sarah.johnson@company.com',
          role: 'operations_manager',
          status: 'active',
          lastLogin: '2 hours ago',
          avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
          department: 'Operations',
          joinDate: '2023-01-15',
          sessionsThisMonth: 45
        },
        {
          id: 2,
          name: 'Mike Chen',
          email: 'mike.chen@company.com',
          role: 'maintenance_engineer',
          status: 'active',
          lastLogin: '1 day ago',
          avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face',
          department: 'Maintenance',
          joinDate: '2023-03-22',
          sessionsThisMonth: 38
        },
        {
          id: 3,
          name: 'Emily Rodriguez',
          email: 'emily.rodriguez@company.com',
          role: 'plant_supervisor',
          status: 'active',
          lastLogin: '3 hours ago',
          avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
          department: 'Production',
          joinDate: '2022-11-10',
          sessionsThisMonth: 52
        },
        {
          id: 4,
          name: 'David Park',
          email: 'david.park@company.com',
          role: 'admin',
          status: 'active',
          lastLogin: '30 minutes ago',
          avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
          department: 'IT',
          joinDate: '2021-09-05',
          sessionsThisMonth: 67
        },
        {
          id: 5,
          name: 'Lisa Wang',
          email: 'lisa.wang@company.com',
          role: 'maintenance_engineer',
          status: 'inactive',
          lastLogin: '1 week ago',
          avatar: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face',
          department: 'Maintenance',
          joinDate: '2023-06-12',
          sessionsThisMonth: 12
        }
      ]);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching users data:', error);
      setLoading(false);
    }
  };

  const StatCard = ({ icon, title, value, subtitle, color = "blue" }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg bg-${color}-100`}>
          <div className={`text-${color}-600`}>{icon}</div>
        </div>
        <div className="ml-4">
          <div className="text-sm font-medium text-gray-500">{title}</div>
          <div className="text-2xl font-bold text-gray-900">{value}</div>
          {subtitle && <div className="text-sm text-gray-600">{subtitle}</div>}
        </div>
      </div>
    </div>
  );

  const getRoleDisplay = (role) => {
    const roleMap = {
      'admin': { label: 'Admin', color: 'red' },
      'operations_manager': { label: 'Operations Manager', color: 'blue' },
      'maintenance_engineer': { label: 'Maintenance Engineer', color: 'green' },
      'plant_supervisor': { label: 'Plant Supervisor', color: 'purple' }
    };
    return roleMap[role] || { label: role, color: 'gray' };
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'green' : 'red';
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const UserActivityChart = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">User Activity Trend</h3>
      <div className="space-y-4">
        {[
          { day: 'Monday', logins: 89, color: PROCOGIA_COLORS.blue },
          { day: 'Tuesday', logins: 95, color: PROCOGIA_COLORS.blue },
          { day: 'Wednesday', logins: 76, color: PROCOGIA_COLORS.blue },
          { day: 'Thursday', logins: 103, color: PROCOGIA_COLORS.green },
          { day: 'Friday', logins: 87, color: PROCOGIA_COLORS.blue },
          { day: 'Saturday', logins: 34, color: PROCOGIA_COLORS.lightGrey },
          { day: 'Sunday', logins: 28, color: PROCOGIA_COLORS.lightGrey }
        ].map((item, index) => (
          <div key={index} className="flex items-center">
            <div className="w-20 text-sm text-gray-600">{item.day}</div>
            <div className="flex-1 ml-4">
              <div className="flex items-center space-x-2">
                <div className="flex-1 bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      width: `${(item.logins / 120) * 100}%`,
                      backgroundColor: item.color
                    }}
                  ></div>
                </div>
                <span className="text-sm text-gray-600 w-8">
                  {item.logins}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const RoleDistributionChart = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Role Distribution</h3>
      <div className="space-y-3">
        {[
          { role: 'operations_manager', count: 35, percentage: 27.6 },
          { role: 'maintenance_engineer', count: 42, percentage: 33.1 },
          { role: 'plant_supervisor', count: 28, percentage: 22.0 },
          { role: 'admin', count: 8, percentage: 6.3 },
          { role: 'other', count: 14, percentage: 11.0 }
        ].map((item, index) => {
          const roleInfo = getRoleDisplay(item.role);
          return (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full bg-${roleInfo.color}-500`}></div>
                <span className="text-sm text-gray-700">{roleInfo.label}</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-900">{item.count}</span>
                <span className="text-xs text-gray-500">({item.percentage}%)</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
          <p className="text-gray-600">Manage user accounts, roles, and permissions</p>
        </div>
        <div className="flex items-center space-x-3">

          <button 
            className="flex items-center space-x-2 text-white px-4 py-2 rounded-lg transition-colors"
            style={{
              backgroundColor: PROCOGIA_COLORS.blue
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = PROCOGIA_COLORS.green;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = PROCOGIA_COLORS.blue;
            }}
          >
            <UserPlus className="w-4 h-4" />
            <span>Add User</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={<Users className="w-6 h-6" />}
          title="Total Users"
          value={stats.totalUsers}
          subtitle="Registered accounts"
          color="blue"
        />
        <StatCard
          icon={<UserCheck className="w-6 h-6" />}
          title="Active Users"
          value={stats.activeUsers}
          subtitle="Currently active"
          color="green"
        />
        <StatCard
          icon={<Shield className="w-6 h-6" />}
          title="Admin Users"
          value={stats.adminUsers}
          subtitle="With admin privileges"
          color="red"
        />
        <StatCard
          icon={<UserPlus className="w-6 h-6" />}
          title="New This Week"
          value={stats.newUsersThisWeek}
          subtitle="Recent registrations"
          color="purple"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UserActivityChart />
        <RoleDistributionChart />
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">User Directory</h3>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="operations_manager">Operations Manager</option>
                <option value="maintenance_engineer">Maintenance Engineer</option>
                <option value="plant_supervisor">Plant Supervisor</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                <th className="text-left py-3 px-6 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((user) => {
                const roleInfo = getRoleDisplay(user.role);
                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-4 px-6">
                      <div className="flex items-center">
                        <img 
                          className="w-10 h-10 rounded-full mr-3" 
                          src={user.avatar} 
                          alt={user.name}
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-${roleInfo.color}-100 text-${roleInfo.color}-800`}>
                        {roleInfo.label}
                      </span>
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                        user.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mr-1 ${
                          user.status === 'active' ? 'bg-green-400' : 'bg-red-400'
                        }`}></div>
                        {user.status}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-900">{user.lastLogin}</td>
                    <td className="py-4 px-6 text-sm text-gray-900">{user.department}</td>
                    <td className="py-4 px-6">
                      <div className="flex items-center space-x-2">
                        <button className="text-blue-600 hover:text-blue-800 p-1">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button className="text-red-600 hover:text-red-800 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                        <button className="text-gray-600 hover:text-gray-800 p-1">
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
};

export default UsersDashboard; 